//@flow
import React, {useState, useEffect} from 'react';

import {
  makeStyles,
  TableCell,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Tooltip,
} from '@material-ui/core';
import {Add, Close, VideoLibrary, Visibility} from '@material-ui/icons';
import {useProductContext} from '../../../context';
import {useEditProduct} from '../../../utils/editProduct';
import {colors} from '../../../../../assets/styles/colors';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import ProductVideoEmbed from '../../../../products/components/Sections/ProductVideoSection/ProductVideoEmbed';
import {
  validateVideoUrl,
  normalizeVideoInput,
} from '../../../../products/components/Sections/ProductVideoSection/videoUtils';

import {ProductVideo} from '../../../../../entities/product';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: '200px',
  },
  chip: {
    fontWeight: 500,
  },
  addIconButton: {
    padding: theme.spacing(0.5),
  },
  modalContent: {
    padding: '1.5rem',
    minWidth: '500px',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1.5rem',
    color: colors.grey900,
  },
  inputContainer: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1.5rem',
    alignItems: 'flex-start',
  },
  textField: {
    flex: 1,
  },
  addButton: {
    marginTop: '0.25rem',
  },
  list: {
    maxHeight: '300px',
    overflow: 'auto',
  },
  listItem: {
    borderBottom: `1px solid ${colors.grey200}`,
  },
  platformLabel: {
    color: colors.primary,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  error: {
    color: colors.error,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  videoActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  previewIcon: {
    color: colors.primary,
  },
  previewModalContent: {
    padding: '1.5rem',
    minWidth: '600px',
  },
  previewModalTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1.5rem',
    color: colors.grey900,
  },
  previewVideoContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
}));

export default function ProductVideoCell() {
  const classes = useStyles();

  const [product, setProduct] = useProductContext(product => ({
    id: product.id,
    videos: product.videos || [],
  }));
  const {editProduct} = useEditProduct(product ? product.id : '');

  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState<ProductVideo[]>(product.videos || []);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [videoToPreview, setVideoToPreview] = useState<ProductVideo | null>(
    null
  );

  useEffect(() => {
    setVideos(product.videos || []);
  }, [product.videos]);

  if (!product) return null;

  const handleAddVideo = () => {
    if (!inputValue.trim()) return;

    setError('');

    const normalizedUrl = normalizeVideoInput(inputValue);

    const validation = validateVideoUrl(normalizedUrl);
    if (!validation.isValid) {
      setError(validation.error || 'URL inválida');
      return;
    }
    const newVideo: ProductVideo = {
      url: normalizedUrl,
      socialMediaType: validation.platform,
    };

    const updatedVideos = [...videos, newVideo];
    setVideos(updatedVideos);
    setInputValue('');
    setError('');

    editProduct({videos: updatedVideos});
    setProduct({videos: updatedVideos});
  };

  const handleDeleteVideo = (videoIndex: number) => {
    const updatedVideos = videos.filter((_, index) => index !== videoIndex);
    setVideos(updatedVideos);

    editProduct({videos: updatedVideos});
    setProduct({videos: updatedVideos});
  };

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddVideo();
    }
  };

  const handleOpenPreview = (video: ProductVideo) => {
    setVideoToPreview(video);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setVideoToPreview(null);
    setPreviewOpen(false);
  };

  return (
    <>
      <TableCell align="right">
        <div className={classes.container}>
          {videos.length > 0 ? (
            <Chip
              icon={<VideoLibrary />}
              label={`${videos.length} video${videos.length > 1 ? 's' : ''}`}
              onClick={() => setOpen(true)}
              className={classes.chip}
              color="primary"
              variant="outlined"
            />
          ) : (
            <Typography variant="caption" color="textSecondary">
              Sin videos
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={() => setOpen(true)}
            className={classes.addIconButton}
          >
            <Add fontSize="small" />
          </IconButton>
        </div>
      </TableCell>

      <CustomModal
        open={open}
        handleCloseModal={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className={classes.modalContent}>
          <Typography className={classes.modalTitle}>
            Videos del producto
          </Typography>

          <div className={classes.inputContainer}>
            <CustomTextField
              className={classes.textField}
              placeholder="Pega URL o código embed"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              multiline
              rows={2}
            />
            <CustomButton
              className={classes.addButton}
              onClick={handleAddVideo}
              disabled={!inputValue.trim()}
            >
              Agregar
            </CustomButton>
          </div>

          {error && <Typography className={classes.error}>{error}</Typography>}

          {videos.length > 0 ? (
            <List className={classes.list}>
              {videos.map((video, index) => (
                <ListItem key={index} className={classes.listItem}>
                  <ListItemText primary={video.url.substring(0, 60) + '...'} />
                  <ListItemSecondaryAction>
                    <div className={classes.videoActions}>
                      <Tooltip title="Ver preview">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenPreview(video)}
                          className={classes.previewIcon}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteVideo(index)}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </div>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              No hay videos agregados
            </Typography>
          )}
        </div>
      </CustomModal>

      <CustomModal open={previewOpen} handleCloseModal={handleClosePreview}>
        <div className={classes.previewModalContent}>
          <Typography className={classes.previewModalTitle}>
            Vista previa del video
          </Typography>
          {videoToPreview && (
            <div className={classes.previewVideoContent}>
              <ProductVideoEmbed
                socialMediaType={videoToPreview.socialMediaType}
                url={videoToPreview.url}
                muted
                loop
              />
            </div>
          )}
        </div>
      </CustomModal>
    </>
  );
}
